<template>
  <div class="bg-white rounded-xl shadow-lg overflow-hidden dark:bg-gray-800">
    <!-- Header -->
    <div
      class="p-6 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-700 relative"
    >
      <div
        class="w-fit flex flex-row gap-5 items-center justify-start portrait:flex-col portrait:items-start"
      >
        <button
          @click="goBack"
          class="p-3 portrait:absolute top-2 right-2 flex items-center justify-center aspect-square w-fit rounded-lg bg-gray-200/50 hover:bg-gray-300/70 dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
          aria-label="Go back"
        >
          <i
            v-if="isLandscape"
            class="fa-solid fa-arrow-left text-base hidden landscape:inline dark:text-gray-200"
          ></i>
          <i
            v-else
            class="fa-solid fa-xmark text-base hidden portrait:inline dark:text-gray-200"
          ></i>
        </button>

        <div class="flex items-center gap-3">
          <div class="p-3 rounded-xl bg-blue-100 dark:bg-blue-900/30">
            <i
              class="fa-solid fa-gears text-2xl text-blue-600 dark:text-blue-400"
            ></i>
          </div>
          <div>
            <h2 class="text-xl font-bold dark:text-white">
              Default Campaign Settings
            </h2>
            <p class="text-sm text-gray-600 dark:text-gray-300 mt-1">
              Set default values for new campaigns to streamline your workflow
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Form -->
    <form class="p-5 space-y-6" @submit.prevent="saveSettings">
      <!-- Product Selection -->
      <div>
        <label class="block text-sm font-medium mb-2 dark:text-gray-200">
          Default Product
        </label>
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-2">
          This product will be selected by default for new campaigns
        </p>
        <select
          v-model="form.product"
          class="w-full h-12 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-100 bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent appearance-none cursor-pointer"
          :class="{ 'border-red-500 dark:border-red-400': errors.product }"
        >
          <option disabled value="" class="text-gray-400 dark:text-gray-100">
            Select a product
          </option>
          <option
            class="text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-800"
            v-for="option in visibleProducts"
            :key="option.id"
            :value="option.id"
          >
            {{ option.name || "" }}
          </option>
        </select>
        <p v-if="errors.product" class="text-red-500 text-xs mt-1">
          {{ errors.product }}
        </p>
        <div v-if="selectedProduct" class="w-full flex flex-wrap gap-1 p-2">
          <span
            class="max-w-[200px] flex w-min overflow-hidden text-ellipsis whitespace-nowrap py-2 px-3 rounded-lg text-sm text-center items-center justify-center relative transition-colors bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
          >
            {{ selectedProduct.name }}
          </span>
        </div>
      </div>

      <!-- Budget Input -->
      <div>
        <label class="block text-sm font-medium mb-2 dark:text-gray-200">
          Default Budget ({{ currencySymbol }})
        </label>
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-2">
          Set the default daily budget for new campaigns
        </p>
        <input
          v-model="form.budget"
          type="number"
          step="0.01"
          min="0"
          inputmode="decimal"
          class="no-spinner w-full h-12 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all duration-200"
          :class="{ 'border-red-500 dark:border-red-400': errors.budget }"
          placeholder="Enter default budget"
        />
        <p v-if="errors.budget" class="text-red-500 text-xs mt-1">
          {{ errors.budget }}
        </p>
      </div>

      <!-- Business Category -->
      <div>
        <label class="block text-sm font-medium mb-2 dark:text-gray-200">
          Business Category
        </label>
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-2">
          Select the default business category for new campaigns
        </p>
        <select
          v-model="form.selectedCategoryId"
          class="w-full h-12 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-100 bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent appearance-none cursor-pointer"
          :class="{
            'border-red-500 dark:border-red-400': errors.selectedCategoryId,
          }"
        >
          <option disabled value="" class="text-gray-400 dark:text-gray-100">
            Select business category
          </option>
          <option
            v-for="category in businessCategories"
            :key="category.id"
            :value="category.id"
            class="text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-800"
          >
            {{ category.category }}
          </option>
        </select>
        <p v-if="errors.selectedCategoryId" class="text-red-500 text-xs mt-1">
          {{ errors.selectedCategoryId }}
        </p>
        <div v-if="selectedCategory" class="w-full flex flex-wrap gap-1 p-2">
          <span
            class="max-w-[200px] flex w-min overflow-hidden text-ellipsis whitespace-nowrap py-2 px-3 rounded-lg text-sm text-center items-center justify-center relative transition-colors bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
          >
            {{ selectedCategory.category }}
          </span>
        </div>
      </div>

      <!-- Business Type -->
      <div v-if="form.selectedCategoryId || form.businessType">
        <label class="block text-sm font-medium mb-2 dark:text-gray-200">
          Business Type
        </label>
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-2">
          Select the default business type for new campaigns
        </p>
        <select
          v-model="form.businessType"
          class="w-full h-12 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-800 dark:text-gray-100 bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent appearance-none cursor-pointer"
          :class="{ 'border-red-500 dark:border-red-400': errors.businessType }"
        >
          <option disabled value="" class="text-gray-400 dark:text-gray-100">
            Select business type
          </option>
          <option
            v-for="type in businessTypes(form.selectedCategoryId)"
            :key="type"
            :value="type"
            class="text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-800"
          >
            {{ type }}
          </option>
        </select>
        <p v-if="errors.businessType" class="text-red-500 text-xs mt-1">
          {{ errors.businessType }}
        </p>
        <div v-if="form.businessType" class="w-full flex flex-wrap gap-1 p-2">
          <span
            class="max-w-[200px] flex w-min overflow-hidden text-ellipsis whitespace-nowrap py-2 px-3 rounded-lg text-sm text-center items-center justify-center relative transition-colors bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
          >
            {{ form.businessType }}
          </span>
        </div>
      </div>

      <!-- Form Actions -->
      <div
        class="flex justify-end space-x-3 pt-6 border-t border-gray-100 dark:border-gray-700"
      >
        <button
          type="button"
          @click="resetForm"
          class="py-3 px-5 inline-flex justify-center items-center gap-2 rounded-xl border font-medium bg-white text-gray-700 shadow-sm align-middle hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-blue-500 transition-all text-sm dark:bg-gray-700 dark:hover:bg-gray-600 dark:border-gray-600 dark:text-gray-200 dark:hover:text-white"
        >
          <i class="fa-solid fa-rotate-left"></i>
          Reset
        </button>
        <button
          type="submit"
          :disabled="isSaving"
          class="py-3 px-5 inline-flex justify-center items-center gap-2 rounded-xl border border-transparent font-semibold bg-blue-600 hover:bg-blue-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed dark:bg-blue-700 dark:hover:bg-blue-800"
        >
          <i class="fa-solid fa-floppy-disk"></i>
          {{ isSaving ? "Saving..." : "Save Changes" }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { mapActions } from "vuex";

export default {
  name: "AdSettingsComponent",
  data() {
    return {
      form: {
        product: "",
        budget: "",
        businessType: "",
        selectedCategoryId: "",
      },
      errors: {
        product: "",
        budget: "",
        businessType: "",
        selectedCategoryId: "",
      },
      isSaving: false,
      businessCategories: [],
      isLandscape: window.matchMedia("(orientation: landscape)").matches,
    };
  },
  props: {
    products: {
      type: Array,
      default: () => [],
    },
    currencySymbol: {
      type: String,
      default: "$",
    },
    defaultData: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    visibleProducts() {
      return this.products
        ?.filter((product) => product.visibility === 1)
        .map((product) => ({
          id: product.id,
          name: product.name,
        }));
    },
    selectedProduct() {
      return (
        this.products.find((p) => Number(p.id) === Number(this.form.product)) ||
        null
      );
    },
    selectedCategory() {
      return (
        this.businessCategories.find(
          (c) => c.id === this.form.selectedCategoryId
        ) ||
        this.businessCategories.find(
          (c) =>
            c.category.toLowerCase() ===
            this.defaultData.data?.business_category?.toLowerCase()
        ) ||
        null
      );
    },
  },
  watch: {
    defaultData: {
      immediate: true,
      handler(newData) {
        if (newData && Object.keys(newData).length > 0) {
          console.log("Default data updated:", newData);
          this.form.product = newData.data.product_id || "";
          this.form.budget = newData.data.budget || "";
          this.form.businessType = newData.data.business_type || "";
        }
      },
    },
  },
  async mounted() {
    await this.loadBusinessCategories();
  },
  methods: {
    ...mapActions(["updateDefault"]),
    async loadBusinessCategories() {
      try {
        const categories = await import("@/data/businessCategories.json");
        this.businessCategories = categories.default || categories;
      } catch (error) {
        console.error("Failed to load business categories:", error);
        toast.error("Failed to load business categories");
      }
    },
    businessTypes(categoryId) {
      const category = this.businessCategories.find((c) => c.id === categoryId);
      return category ? category.types : [];
    },
    goBack() {
      this.$emit("close-settings");
    },
    async saveSettings() {
      this.isSaving = true;

      try {
        const payload = {
          selectedProduct: this.form.product,
          selectedBudget: this.form.budget || 0,
          selectedCategory: this.selectedCategory.category || "",
          selectedType: this.form.businessType || "",
        };
        const response = await this.updateDefault(payload);

        if (response.success) {
          toast.success(
            response.data.message || "Settings saved successfully!"
          );
        } else {
          toast.error(response.data.message || "Failed to save settings");
        }
      } catch (error) {
        console.error("Failed to save settings:", error);
        toast.error("Failed to save settings. Please try again.");
      } finally {
        this.isSaving = false;
      }
    },
    resetForm() {
      this.form = {
        product: "",
        budget: "",
        businessType: "",
        selectedCategoryId: "",
      };
      this.errors = {
        product: "",
        budget: "",
        businessType: "",
        selectedCategoryId: "",
      };
    },
  },
};
</script>

<style scoped>
.no-spinner::-webkit-outer-spin-button,
.no-spinner::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.no-spinner {
  -moz-appearance: textfield;
}
</style>
